import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faXmark, faGift, faArrowRightLong } from "@fortawesome/free-solid-svg-icons";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import useSectionNav from "../../hooks/useSectionNav";
import "./NavBar.scss";

const NAV_LINKS = [
  { href: "#features", key: "landing.nav.features" },
  { href: "#devices",  key: "landing.nav.devices" },
  { href: "#pricing",  key: "landing.nav.pricing" },
  { href: "#trust",    key: "landing.nav.testimonials" },
];

const PROMO_DISMISSED_KEY = "syd_promo_bar_dismissed";

const NavBar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const onSectionClick = useSectionNav();
  const [scrolled,   setScrolled]   = useState(false);
  const [progress,   setProgress]   = useState(0);
  const [activeHash, setActiveHash] = useState("");
  const [showLoginMenu, setShowLoginMenu] = useState(false);
  // Mobile nav is a proper off-canvas sidebar (see .navbar-collapse mobile rules in
  // NavBar.scss) rather than Bootstrap's default dropdown-under-the-navbar collapse, so
  // its open state is plain React state instead of Bootstrap's JS collapse plugin — that
  // still toggles the same `.show` class Bootstrap's own CSS already keys off of.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const navRef = useRef(null);
  const loginMenuRef = useRef(null);

  // "1 mois gratuit" promo strip; storage can throw (private mode, blocked site data)
  const [showPromo, setShowPromo] = useState(() => {
    try { return localStorage.getItem(PROMO_DISMISSED_KEY) !== "1"; } catch { return true; }
  });
  const dismissPromo = () => {
    setShowPromo(false);
    try { localStorage.setItem(PROMO_DISMISSED_KEY, "1"); } catch { /* not persisted, fine */ }
  };

  useEffect(() => {
    if (!showLoginMenu) return;
    const handleClickOutside = (e) => {
      if (loginMenuRef.current && !loginMenuRef.current.contains(e.target)) setShowLoginMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLoginMenu]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let lastY   = window.scrollY;
    let hidden  = false;

    const show = () => { nav.style.top = "0px";   hidden = false; };
    const hide = () => { nav.style.top = "-80px";  hidden = true;  };

    const onScroll = () => {
      const y = window.scrollY;

      if (y < 80) {
        if (hidden) show();
      } else if (y > lastY && !hidden) {
        hide();
      } else if (y < lastY && hidden) {
        show();
      }

      lastY = y;

      setScrolled(y > 40);
      setProgress((() => {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        return h > 0 ? (y / h) * 100 : 0;
      })());

      let current = "";
      for (const { href } of NAV_LINKS) {
        const el = document.getElementById(href.slice(1));
        if (el && el.getBoundingClientRect().top <= 100) current = href.slice(1);
      }
      setActiveHash(current);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On the landing page — trailing slash ignored, since the server redirects /fr to /fr/
  // (NavLink's own `end` matching would then miss it)
  const onLanding = location.pathname.replace(/\/+$/, "") === `/${i18n.language}`;

  // Shared between the desktop inline nav and the mobile off-canvas sidebar (portalled
  // below) so the two don't drift out of sync — only the surrounding chrome differs.
  const navLinksContent = (
    <>
      <ul className="syd-navbar__links mx-auto">
        {/* home: back to the landing (or to its top when already there) */}
        <li>
          <NavLink
            to={`/${i18n.language}`}
            onClick={() => { if (onLanding) window.scrollTo({ top: 0, behavior: "smooth" }); closeSidebar(); }}
            className={`syd-navbar__link syd-navbar__link--home${onLanding && !activeHash ? " syd-navbar__link--active" : ""}`}
            aria-label={t("landing.nav.home")}
          >
            <span className="syd-navbar__link-label">{t("landing.nav.home")}</span>
          </NavLink>
        </li>
        {NAV_LINKS.map(({ href, key }) => (
          <li key={href}>
            <a
              href={onLanding ? href : `/${i18n.language}${href}`}
              onClick={(e) => { onSectionClick(href.slice(1))(e); closeSidebar(); }}
              className={`syd-navbar__link${activeHash === href.slice(1) ? " syd-navbar__link--active" : ""}`}
            >
              {t(key)}
            </a>
          </li>
        ))}
        <li>
          <NavLink
            to={`/${i18n.language}/contact`}
            onClick={closeSidebar}
            className={({ isActive }) => `syd-navbar__link${isActive ? " syd-navbar__link--active" : ""}`}
          >
            {t("landing.nav.contact")}
          </NavLink>
        </li>
      </ul>

      <LanguageSelector className="syd-navbar__lang" />

      <div className="syd-navbar__actions">
        <div className="syd-navbar__login-group" ref={loginMenuRef}>
          <NavLink to={`/${i18n.language}/login`} onClick={closeSidebar} className="syd-navbar__cta syd-navbar__cta--login">{t("landing.nav.login")}</NavLink>
          <button
            type="button"
            className="syd-navbar__login-caret"
            onClick={() => setShowLoginMenu(v => !v)}
            aria-expanded={showLoginMenu}
            aria-label={t("login.create_account")}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>

          {showLoginMenu && (
            <div className="syd-navbar__login-dropdown">
              <NavLink to={`/${i18n.language}/register`} className="syd-navbar__login-dropdown-item" onClick={() => { setShowLoginMenu(false); closeSidebar(); }}>
                {t("login.create_account")}
              </NavLink>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
    {/* Promo strip above the navbar on every public page using it — in normal flow, so it
        scrolls away while the navbar turns fixed. Dismissal remembered per browser. */}
    {showPromo && (
      <div className="syd-promo-bar">
        <FontAwesomeIcon icon={faGift} className="syd-promo-bar__icon" />
        <span>{t("landing.promo.text")}</span>
        <NavLink to={`/${i18n.language}/register`} className="syd-promo-bar__cta">
          {t("landing.promo.cta")} <FontAwesomeIcon icon={faArrowRightLong} />
        </NavLink>
        <button type="button" className="syd-promo-bar__close" onClick={dismissPromo} aria-label={t("landing.nav.close")}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    )}
    <nav
      ref={navRef}
      className={`syd-navbar navbar navbar-expand-lg${scrolled ? " syd-navbar--scrolled" : ""}`}
    >
      <div className="syd-navbar__progress" style={{ width: `${progress}%` }} />

      <div className="container-fluid px-4 px-lg-5">
        <a className="syd-navbar__brand" href={`/${i18n.language}`}>
          <img src="/images/saydalani_logo.png" alt="Saydalani" className="syd-navbar__brand-logo" />
        </a>

        <button
          className="syd-navbar__toggler navbar-toggler"
          type="button"
          onClick={() => setSidebarOpen(o => !o)}
          aria-expanded={sidebarOpen}
          aria-controls="navbarContent"
          aria-label={t("landing.nav.menu")}
        >
          <span className="syd-navbar__toggler-bar" />
          <span className="syd-navbar__toggler-bar" />
          <span className="syd-navbar__toggler-bar" />
        </button>

        {/* Desktop inline nav — Bootstrap's navbar-expand-lg forces this visible as a
            normal flex row at ≥992px regardless of the collapse/show state below; it's
            hidden below that breakpoint in favor of the portalled sidebar instead
            (see .navbar-collapse rules in NavBar.scss). */}
        <div className="collapse navbar-collapse d-none d-lg-flex" id="navbarContent">
          {navLinksContent}
        </div>
      </div>

      {/* Mobile off-canvas sidebar — portalled to <body> so it isn't clipped by the
          navbar's own backdrop-filter, which creates a new containing block for any
          position:fixed descendant (the sidebar would otherwise be confined to the
          navbar's own ~64px-tall box instead of the full viewport). */}
      {sidebarOpen && createPortal(
        <>
          <div className="syd-navbar__backdrop" onClick={closeSidebar} />
          <div className="collapse navbar-collapse show syd-navbar__sidebar-portal" id="navbarContentMobile">
            <button type="button" className="syd-navbar__sidebar-close" onClick={closeSidebar} aria-label={t("landing.nav.close")}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            {navLinksContent}
          </div>
        </>,
        document.body
      )}
    </nav>
    </>
  );
};

export default NavBar;
